import { ArrowLeft } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { cn } from "@/lib/utils";

/** A quiet header link: the way back to the list, and a record queue's ends. */
export const RECORD_HEADER_LINK =
  "inline-flex min-h-[var(--control-height-compact)] w-fit items-center gap-1.5 rounded-control text-meta font-semibold text-muted-foreground outline-none transition-colors duration-[var(--motion-fast)] ease-[var(--ease-standard)] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring";

/**
 * The identity of every registrar record page (a section, a person, an
 * applicant file, an invitation): the way back to its list, then the record as
 * the page's `h2` under the registrar frame's one `h1`, one meta line, and its
 * status. It carries facts only, never an act; `nav` sits beside the way back
 * for a record that is read in a queue. Until the record loads only the way
 * back shows.
 */
export function RegistrarRecordHeader({
  listHref,
  listLabel,
  eyebrow,
  title,
  meta,
  status,
  nav,
}: {
  listHref: string;
  listLabel: string;
  eyebrow: string;
  title?: ReactNode;
  meta?: ReactNode;
  status?: ReactNode;
  nav?: ReactNode;
}) {
  return (
    <header data-slot="page-frame" className="grid min-w-0 gap-3">
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <Link to={listHref} className={RECORD_HEADER_LINK}>
          <ArrowLeft className="size-4" strokeWidth={1.75} aria-hidden="true" />
          {listLabel}
        </Link>
        {nav}
      </div>
      {title !== undefined && (
        <div className="grid min-w-0 gap-2">
          <p className="eyebrow">{eyebrow}</p>
          <h2 className="min-w-0 text-tile-title font-semibold text-foreground [overflow-wrap:anywhere]">
            {title}
          </h2>
          {(meta || status) && (
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {meta && <p className="min-w-0 text-ui text-muted-foreground">{meta}</p>}
              {status}
            </div>
          )}
        </div>
      )}
    </header>
  );
}

export interface RegistrarRecordView {
  key: string;
  label: string;
  to: string;
  state?: unknown;
}

/**
 * The one view tab row of a registrar record: a row of links, the record is
 * the path and the view its `?view=`, `aria-current="page"` on the active one.
 */
export function RegistrarRecordViews({
  label,
  views,
  active,
}: {
  label: string;
  views: readonly RegistrarRecordView[];
  active: string;
}) {
  return (
    <nav aria-label={label} className="flex min-w-0 flex-wrap items-center gap-1 border-b border-rule py-2">
      {views.map((view) => (
        <Link
          key={view.key}
          to={view.to}
          state={view.state}
          aria-current={view.key === active ? "page" : undefined}
          className={cn(
            "inline-flex min-h-[var(--control-height-compact)] shrink-0 items-center rounded-control px-3 text-meta font-semibold transition-colors duration-[var(--motion-fast)] ease-[var(--ease-standard)] outline-none focus-visible:ring-2 focus-visible:ring-ring",
            view.key === active
              ? "bg-primary-wash text-primary"
              : "text-muted-foreground hover:bg-well hover:text-foreground",
          )}
        >
          {view.label}
        </Link>
      ))}
    </nav>
  );
}
